/**
 * useOrderSearch Hook
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  OrderFilters,
  PaginationState,
  OrderSearchRequest,
  OrderSearchResponse,
  OrderSearchResultItem,
} from '../types/filter.types';

const COMMERCE_SERVICE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL ||
  'http://localhost:8083/commerce-service';

function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

function buildSearchRequest(
  filters: OrderFilters,
  pagination: PaginationState
): OrderSearchRequest {
  const request: OrderSearchRequest = {
    page: pagination.page - 1,
    pageSize: pagination.pageSize,
  };

  if (filters.searchTerm) {
    request.searchTerm = filters.searchTerm;
  }
  if (filters.statuses.length > 0) {
    request.statuses = filters.statuses;
  }
  if (filters.orderDateRange.from) {
    request.orderDateFrom = filters.orderDateRange.from;
  }
  if (filters.orderDateRange.to) {
    request.orderDateTo = filters.orderDateRange.to;
  }
  if (filters.expectedDeliveryDateRange.from) {
    request.expectedDeliveryFrom = filters.expectedDeliveryDateRange.from;
  }
  if (filters.expectedDeliveryDateRange.to) {
    request.expectedDeliveryTo = filters.expectedDeliveryDateRange.to;
  }
  if (filters.actualDeliveryDateRange.from) {
    request.actualDeliveryFrom = filters.actualDeliveryDateRange.from;
  }
  if (filters.actualDeliveryDateRange.to) {
    request.actualDeliveryTo = filters.actualDeliveryDateRange.to;
  }
  if (filters.amountRange.min !== null) {
    request.minAmount = filters.amountRange.min;
  }
  if (filters.amountRange.max !== null) {
    request.maxAmount = filters.amountRange.max;
  }
  if (filters.customerType !== 'ALL') {
    request.customerType = filters.customerType;
  }
  if (filters.location.state) {
    request.state = filters.location.state;
  }
  if (filters.location.city) {
    request.city = filters.location.city;
  }

  return request;
}

async function searchOrders(
  request: OrderSearchRequest
): Promise<OrderSearchResponse> {
  const token = getAuthToken();

  const queryParams = new URLSearchParams();
  queryParams.append('page', (request.page).toString());
  queryParams.append('pageSize', request.pageSize.toString());

  if (request.searchTerm) {
    queryParams.append('searchTerm', request.searchTerm);
  }
  if (request.statuses && request.statuses.length > 0) {
    request.statuses.forEach(status => {
      queryParams.append('statuses', status);
    });
  }
  if (request.orderDateFrom) {
    queryParams.append('orderDateFrom', request.orderDateFrom);
  }
  if (request.orderDateTo) {
    queryParams.append('orderDateTo', request.orderDateTo);
  }
  if (request.expectedDeliveryFrom) {
    queryParams.append('expectedDeliveryFrom', request.expectedDeliveryFrom);
  }
  if (request.expectedDeliveryTo) {
    queryParams.append('expectedDeliveryTo', request.expectedDeliveryTo);
  }
  if (request.actualDeliveryFrom) {
    queryParams.append('actualDeliveryFrom', request.actualDeliveryFrom);
  }
  if (request.actualDeliveryTo) {
    queryParams.append('actualDeliveryTo', request.actualDeliveryTo);
  }
  if (request.minAmount !== undefined && request.minAmount !== null) {
    queryParams.append('minAmount', request.minAmount.toString());
  }
  if (request.maxAmount !== undefined && request.maxAmount !== null) {
    queryParams.append('maxAmount', request.maxAmount.toString());
  }
  if (request.customerType) {
    queryParams.append('customerType', request.customerType);
  }
  if (request.state) {
    queryParams.append('state', request.state);
  }
  if (request.city) {
    queryParams.append('city', request.city);
  }

  const response = await fetch(
    COMMERCE_SERVICE_URL + '/api/v1/orders?' + queryParams.toString(),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: 'Bearer ' + token }),
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to search orders');
  }

  const apiResponse = await response.json();
  
  // Map backend response to expected format
  // Backend returns: { success, message, data: { content, page, size, totalElements, totalPages } }
  const backendData = apiResponse.data || apiResponse;
  
  return {
    orders: backendData.content || [],
    totalElements: backendData.totalElements || 0,
    totalPages: backendData.totalPages || 0,
    currentPage: backendData.page || 0,
    pageSize: backendData.size || 20,
    hasNext: !backendData.last && !backendData.empty,
    hasPrevious: !backendData.first && !backendData.empty,
  };
}

export interface UseOrderSearchReturn {
  orders: OrderSearchResultItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrderSearch(
  filters: OrderFilters,
  pagination: PaginationState
): UseOrderSearchReturn {
  const [orders, setOrders] = useState<OrderSearchResultItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const request = buildSearchRequest(filters, pagination);
      const response = await searchOrders(request);

      setOrders(response.orders);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage + 1);
      setPageSize(response.pageSize);
      setHasNext(response.hasNext);
      setHasPrevious(response.hasPrevious);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch orders';
      setError(errorMessage);
      console.error('[useOrderSearch] Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    totalElements,
    totalPages,
    currentPage,
    pageSize,
    hasNext,
    hasPrevious,
    isLoading,
    error,
    refetch: fetchOrders,
  };
}
